import { createAsyncThunk } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'
import { AxiosError } from '@astronautsid/wpe-utils'

import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import { AppDispatchType } from 'store'
import {
  getMPStaffListAPI,
  putMPStaffStatusAPI,
  getTerminationReasonsAPI,
  putTerminationMPStaffAPI,
  putRejoinMPStaffAPI,
  getVehicleTypeAPI,
  getEmploymentTypeAPI,
  getStaffVendorListAPI,
  getDepartementListPI,
  getLocationListAPI,
  getStaffRoleListAPI,
  createMPStaffAPI,
  putMPStaffAPI,
  exportMPStaffAPI,
  postUpdateActiveRoleAPI,
  getLocationTypeAPI,
  getJobIDsAPI,
  type MPStaffRequestPayloadType,
  type PutResignationPayloadType,
  type FetchLocationListRequestType,
  type PutMPStaffRequestType,
  type FetchMPStaffQueryType,
  type MPStaffVehicleType,
  type FetchJobIDsParamsType,
  MPStaffType,
  FetchStaffRequestType,
} from 'features/Enterprise/Account/services'

import {
  sliceName,
  setLoading,
  setStaffList,
  setUpdateStaffStatus,
  setResignationReasons,
  setVehicleType,
  setEmploymentType,
  setStaffVendorList,
  setDepartementList,
  setLocationList,
  setRolesList,
  setQuery,
  setLocationTypes,
  setJobIDs,
} from './mpStaffSlice'

export const actSetQuery = (query: FetchMPStaffQueryType) => async (dispatch: AppDispatchType) => {
  await dispatch(setQuery(query))
  dispatch(fetchMPStaffList(MP_STAFF_LIST_MODULE))
}

export const MP_STAFF_LIST_MODULE: NonNullable<FetchStaffRequestType['module']> = {
  staff_attributes: true,
  staff_vendor: true,
  termination_status: true,
  vehicle: true,
}

const parsedLocationType = (type: string, isGetRoles?: boolean) => {
  let locationType = type
  if (type === 'MEGA HUB') locationType = 'MEGAHUB'
  if (type === 'MEGA HUB 2.0') locationType = isGetRoles ? 'MEGAHUB_2_0' : 'MEGAHUB 2.0'
  return locationType
}

export const fetchMPStaffList = createAsyncThunk(
  `${sliceName}/fetchStaffList`,
  async (module: NonNullable<FetchStaffRequestType['module']>, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))

      const {
        mpStaff: { query },
      } = getState() as StoreStateType

      const { data } = await getMPStaffListAPI({
        location_types: query?.locationType ? [parsedLocationType(query.locationType)] : [],
        location_ids: query?.locationId ? [query.locationId] : [],
        page_index: Number(query?.pageIndex || 1),
        page_size: Number(query?.pageSize || 10),
        name: query?.name?.replaceAll('%20', ' '),
        roles: query?.role ? [`AUTH_${query?.role}`] : [],
        module,
        staff_ids: query?.staffId ? [query.staffId] : [],
      })

      dispatch(setStaffList(data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchMPStaffDetail = createAsyncThunk(
  `${sliceName}/fetchStaffDetail`,
  async (
    { staffId, module }: { staffId: number; module: FetchStaffRequestType['module'] },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getMPStaffListAPI({
        page_index: 1,
        page_size: 1,
        module,
        staff_ids: [String(staffId)],
      })

      return data.data?.[0] as MPStaffType
    } catch (error) {
      callErrorMsg(error)
      return error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putMPStaffStatus = createAsyncThunk(
  `${sliceName}/putDriverStatus`,
  async ({ id, isAvailable }: { id: number; isAvailable: boolean }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await putMPStaffStatusAPI(id, isAvailable)
      dispatch(setUpdateStaffStatus({ id, status: isAvailable ? 'Available' : 'Not Available' }))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchResignationReasons = createAsyncThunk(
  `${sliceName}/fetchResignationReasons`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getTerminationReasonsAPI()

      dispatch(setResignationReasons(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const putTerminationMPStaff = createAsyncThunk(
  `${sliceName}/putTerminationMPStaff`,
  async (
    {
      staffId,
      staffName,
      payload,
    }: { staffId: number; staffName: string; payload: PutResignationPayloadType },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putTerminationMPStaffAPI(staffId, payload)
      toastSuccess(`${staffName} berhasil terupdate`)
      dispatch(fetchMPStaffList(MP_STAFF_LIST_MODULE))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putRejoinMPStaff = createAsyncThunk(
  `${sliceName}/putRejoinMPStaff`,
  async (
    {
      staffId,
      staffName,
      payload,
    }: { staffId: number; staffName: string; payload: PutResignationPayloadType },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putRejoinMPStaffAPI(staffId, payload)
      toastSuccess(`${staffName} berhasil terupdate`)
      dispatch(fetchMPStaffList(MP_STAFF_LIST_MODULE))
    } catch (error) {
      const axiosError = error as AxiosError
      toastFailed(axiosError.message)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchVehicleType = createAsyncThunk(
  `${sliceName}/fetchVehicleType`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getVehicleTypeAPI()
      const { vehicle_type, vehicle_sub_type } = data.data
      const mapResponse = {
        vehicle_type: Object.keys(vehicle_type)?.map((key: string) => ({
          key,
          value: vehicle_type[key as keyof typeof vehicle_type],
        })),
        vehicle_sub_type: Object.keys(vehicle_sub_type)?.map((key: string) => ({
          key,
          value: String(vehicle_sub_type[key as keyof typeof vehicle_sub_type]),
        })),
      }
      dispatch(setVehicleType(mapResponse as never as MPStaffVehicleType))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchEmploymentType = createAsyncThunk(
  `${sliceName}/fetchEmploymentType`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getEmploymentTypeAPI()
      dispatch(setEmploymentType(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchStaffVendorList = createAsyncThunk(
  `${sliceName}/fetchStaffVendorList`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getStaffVendorListAPI()
      dispatch(setStaffVendorList(data?.data || []))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchDepartementList = createAsyncThunk(
  `${sliceName}/fetchDepartementList`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getDepartementListPI()
      dispatch(setDepartementList(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchLocationList = createAsyncThunk(
  `${sliceName}/fetchLocationList`,
  async (params: FetchLocationListRequestType, { dispatch }) => {
    try {
      const { data } = await getLocationListAPI({
        ...params,
        type: parsedLocationType(params?.type || ''),
      })
      dispatch(setLocationList(data.content))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchMPStaffRoleList = createAsyncThunk(
  `${sliceName}/fetchMPStaffRoleList`,
  async (locationType: string, { dispatch }) => {
    try {
      const { data } = await getStaffRoleListAPI({
        locationType: parsedLocationType(locationType, true),
      })
      dispatch(setRolesList(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const createMPStaff = createAsyncThunk(
  `${sliceName}/createMPStaff`,
  async ({ payload }: { payload: MPStaffRequestPayloadType }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await createMPStaffAPI(payload)
      toastSuccess('Data MP Staff baru berhasil dicreate')
      return true
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const editMPStaff = createAsyncThunk(
  `${sliceName}/editMPStaff`,
  async ({ payload }: { payload: PutMPStaffRequestType }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await putMPStaffAPI(payload)
      toastSuccess('Data MP Staff berhasil diupdate')
      return true
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const exportMPStaff = createAsyncThunk(
  `${sliceName}/exportMPStaff`,
  async (location_type: string, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))
      const {
        mpStaff: { query },
      } = getState() as StoreStateType

      const params: { location_type: string; location_id?: number } = {
        location_type: location_type || query?.locationType || '',
      }

      if (!location_type) {
        params.location_id = query?.locationId || 0
      }

      const { data } = await exportMPStaffAPI(params)
      saveAs(data.data.file_url)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const updateActiveRole = createAsyncThunk(
  `${sliceName}/updateActiveRole`,
  async ({ staffId, role }: { staffId: number; role: string }, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))
      const {
        auth: { userData },
      } = getState() as StoreStateType
      const { data } = await postUpdateActiveRoleAPI(staffId, role, userData.full_name)

      if (data.error?.message) {
        toastFailed(data.error?.message)
      } else {
        toastSuccess('Role berhasil diupdate')
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchLocationTypes = createAsyncThunk(
  `${sliceName}/fetchLocationTypes`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getLocationTypeAPI()
      dispatch(setLocationTypes(data.data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchJobIds = createAsyncThunk(
  `${sliceName}/fetchJobIds`,
  async (params: FetchJobIDsParamsType, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getJobIDsAPI(params)
      dispatch(setJobIDs(data.data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)
